/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import { contractEditFields } from "../../../constants/forms/contractFields";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
  transformFormDataForApi,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditContractPage = () => {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();

  // Estado para controlar se os dados iniciais já foram carregados no formulário
  const [isDataLoadedIntoForm, setIsDataLoadedIntoForm] = useState(false);

  // Inicializa useForm. Os dados virão do useQuery e serão formatados.
  const [contractSection] = contractEditFields;
  const { formData, handleChange, formatData, setFormData } = useForm(
    setDefaultFieldValues([contractSection]),
    [contractSection],
  );

  // --- 1. Query para buscar os detalhes do Contrato ---
  const {
    data: contractData,
    isLoading: isLoadingContract,
    isError: isErrorContract,
    error: contractQueryError,
  } = useQueryWithLoader({
    queryKey: ["contract", contractId],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.contracts.getById(contractId),
      );
      return response.result;
    },
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: contractInfosData } = useQueryWithLoader({
    queryKey: ["contract_infos", contractId],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.contracts.infos(contractId),
      );
      return response.result;
    },
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
    select: (data) => ({
      duration: data.duration,
      max_duration: data.max_duration,
      start_date: data.start_date,
      end_date: data.end_date,
      max_end_date: data.max_end_date,
    }),
  });

  useEffect(() => {
    if (isErrorContract && contractQueryError) {
      console.error(
        "EditContractPage: Erro ao carregar contrato:",
        contractQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar contrato: ${
          contractQueryError.response?.data?.message ||
          contractQueryError.message
        }`,
      );
      hideLoader();
      navigate(`/aluguel/contratos`, {
        replace: true,
        state: {
          type: "error",
          message: "Contrato não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [isErrorContract, contractQueryError]);

  const contractTypeSelect = useRemoteSelectOptions({
    fieldId: "contract_type_id",
    queryKey: ["contract_types"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      });

      const params = { filters, page, perPage };

      const response = await baseServiceGet(entities.contractTypes.get, params);
      return response.result;
    },
    formData,
    setFormData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  });

  const contractStatusSelect = useRemoteSelectOptions({
    fieldId: "contract_status_id",
    queryKey: ["contract_statuses"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      });

      const params = { filters, page, perPage };

      const response = await baseServiceGet(
        entities.contractStatus.get,
        params,
      );
      return response.result;
    },
    formData,
    setFormData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  });

  const organizationSelect = useRemoteSelectOptions({
    fieldId: "organization_id",
    queryKey: ["organizations"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      });

      const params = { filters, page, perPage };

      const response = await baseServiceGet(entities.organizations.get, params);
      return response.result;
    },
    formData,
    setFormData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
  });

  const customerSelect = useRemoteSelectOptions({
    fieldId: "customer_id",
    queryKey: ["customers"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
        ...(search && { alias: search }),
      });

      const params = { filters, page, perPage };

      const response = await baseServiceGet(entities.customers.get, params);
      return response.result;
    },
    formData,
    setFormData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    refetchOnWindowFocus: true,
  });

  const recipientCustomerSelect = useRemoteSelectOptions({
    queryKey: ["recipientCustomerOptions"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
        parent_id: customerSelect.selectedValue?.value,
        ...(search && { alias: search }),
      });

      const params = {
        filters,
        page,
        perPage,
        sort: { column: "id", order: "desc" },
      };

      const response = await baseServiceGet(entities.customers.get, params);
      return response.result;
    },
    fieldId: "recipient_customer_ids",
    formData,
    isMulti: true,
    enabled: !!customerSelect.selectedValue?.value,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
    mapToOption: (type) => ({
      value: type.id,
      label: `${type.id} - ${type.alias}`,
    }),
  });

  const selects = {
    contract_type_id: contractTypeSelect,
    contract_status_id: contractStatusSelect,
    organization_id: organizationSelect,
    customer_id: customerSelect,
    recipient_customer_ids: recipientCustomerSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Efeito para popular o formulário uma única vez após carregar os dados do contrato ---
  useEffect(() => {
    if (
      !isLoadingContract &&
      !isErrorContract &&
      contractData &&
      contractInfosData &&
      !isDataLoadedIntoForm
    ) {
      // Usamos `formatData` do `useForm` para popular os campos comuns
      formatData(contractData, contractEditFields);

      // Em seguida, ajustamos manualmente os campos de select para o formato { value, label }
      // Isso é importante porque `contractData` vem com IDs, mas o `formData` precisa dos objetos completos
      setFormData((prev) => ({
        ...prev,
        contract_type_id: {
          value: contractData.contract_type_id,
          label: contractData.contract_type,
        },
        contract_status_id: {
          value: contractData.contract_status_id,
          label: contractData.contract_status,
        },
        organization_id: {
          value: contractData.organization_id,
          label: contractData.organization_name,
        },
        customer_id: {
          value: contractData.customer_id,
          label: contractData.customer_name,
        },
        recipient_customer_ids: contractData.recipient_customers.map(
          (item) => ({
            value: item.id,
            label: `${item.id} - ${item.alias}`,
          }),
        ),

        // * informações
        ...(contractInfosData && {
          info: contractInfosData,
        }),
      }));
      setIsDataLoadedIntoForm(true); // Marca que os dados foram carregados
    }
  }, [
    isLoadingContract,
    isErrorContract,
    contractData,
    contractInfosData,
    formatData,
    setFormData,
    isDataLoadedIntoForm,
  ]);

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects],
  );
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects],
  );

  // --- Mutação para atualizar o Contrato ---
  const updateContractMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const transformedData = transformFormDataForApi(dataToUpdate);
      const response = await baseServicePut(
        entities.contracts.update(contractId),
        transformedData,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["contract", contractId] });
        queryClient.invalidateQueries({ queryKey: ["contracts"] });
        navigate("/aluguel/contratos");
      }
    },
    onError: (err) => {
      console.log("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedPayload = cleanPayload(formData);
      const payload = {
        ...cleanedPayload,
        number: String(cleanedPayload.number),
        recipient_customer_ids: cleanPayload(
          cleanedPayload.recipient_customer_ids,
        ),
      };
      await updateContractMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar contrato:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateContractMutation]);

  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos`);
  }, [navigate]);

  // --- Efeito para exibir notificações de estado de localização (PADRÃO) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const isPageLoading = isLoadingContract || updateContractMutation.isPending;

  useEffect(() => {
    if (!isLoadingContract || !updateContractMutation.isPending) {
      hideLoader();
    }
  }, [isLoadingContract, updateContractMutation.isPending, hideLoader]);

  const renderForm = () => {
    return (
      <div key={contractSection.section}>
        <SectionHeader section={contractSection.section} />
        <SimpleBody
          fields={contractSection.fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      </div>
    );
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Contrato"
        showBackButton={true}
        backUrl="/aluguel/contratos"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Salvar"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={isPageLoading}>
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditContractPage;
